/**
 * Каталог макетов v2 — API-клиент.
 *
 * Основные данные — те же эндпоинты design-service, что и у старой страницы.
 * Теги и фасеты загружаются отдельными запросами; при недоступности
 * фронт деградирует без остановки основного каталога.
 */

import { api } from '../../shared/api/client'
import type { DesignFacets, DesignProductionFiles, DesignTag } from './types'

/** Гейтвей отдаёт { success, data } — разворачиваем до data. */
const unwrap = (r: any) => r.data?.data ?? r.data

export interface DesignsV2ListParams {
  limit: number
  offset: number
  search?: string
  type?: string
  category?: string
  brand?: string
  gender?: string
  seasonal?: string
  dateFrom?: string
  dateTo?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  tags?: string
  noTags?: string
  design_state?: string
  pipeline_status?: string
  legal_status?: string
  seo_status?: string
  age?: string
  occasion?: string
  theme_number?: string
  completeness?: 'full' | 'incomplete' | 'no_products'
  hasPreview?: 'true' | 'false'
  attention?: 'true'
  /** Список кодов макетов и/или артикулов товаров через запятую (фильтр по списку). */
  articles?: string
  /** Режим проверки списка: вместе с articles остальные фильтры игнорируются сервером. */
  articles_resolve?: string
}

export type DesignsV2FilterParams = Omit<DesignsV2ListParams, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>

export type DesignSelectionPayload =
  | { design_codes: Array<number | string> }
  | { filters: DesignsV2FilterParams }

export interface DesignFilterOptions {
  types: string[]
  categories: string[]
  brands: string[]
  genders: string[]
  ages: string[]
  occasions: string[]
  legalStatuses: string[]
  designStates: string[]
  seasons: string[]
  languages: string[]
  recipientStatuses: string[]
  personas: string[]
  seoStatuses: string[]
}

export type DesignExportFormat = 'csv' | 'xlsx'

/** Карточка МП, из-за которой полное удаление запрещено */
export interface DesignPurgeBlockingCard {
  id: number
  article: string
  marketplace: string
  sellerId: number
  externalId: string | null
  status: string
  publicationMode: string
  publishedAt: string | null
}

/** Инвентарь макета перед полным удалением: что исчезнет и что мешает */
export interface DesignPurgePreviewItem {
  designCode: string
  exists: boolean
  softDeleted: boolean
  designName: string | null
  themeNumber: number | null
  type: string | null
  articles: string[]
  products: number
  cardsTotal: number
  cardsDraft: number
  blockingCards: DesignPurgeBlockingCard[]
  media: number
  artifacts: number
  pipelineEvents: number
  accrualAmount: number
  sourceFiles: string[]
  blockers: string[]
}

export interface DesignPurgeResultItem {
  designCode: string
  purged: boolean
  blockers: string[]
  db?: Record<string, number>
  filesRemoved: number
  warnings: string[]
}

export interface DesignPurgeResult {
  items: DesignPurgeResultItem[]
  /** Общие проблемы уборки файлов: недоступный FTP, хранилище, media-gen */
  assetWarnings: string[]
  purged: number
  total: number
}

/** Больше за раз бэкенд не примет — операция синхронная и необратимая */
export const PURGE_MAX_DESIGNS = 50

/** Сводка расхождения каталога с матрицей типов макетов */
export interface DesignKitsPreview {
  designsIncomplete: number
  productsMissing: number
  designsWithoutSnapshot: number
  byDesignType: Array<{ design_type: string; designs: number; products: number }>
}

export interface DesignKitsRefreshStart {
  jobId: string
  designsToProcess: number
  productsMissing?: number
  message?: string
}

export type ReplaceDesignSourceMode = 'designs_only' | 'designs_and_products' | 'full_pipeline'

export interface ReplaceDesignSourceSkippedCard {
  id: number
  article: string
  marketplace: string
  seller_id: number
  status: string
  reason: string
}

export interface ReplaceDesignSourceStart {
  jobId: string
  design_code: string
  mode: ReplaceDesignSourceMode
  skipped: ReplaceDesignSourceSkippedCard[]
  /**
   * «Отправить новые фото в опубликованные карточки» (C2, SPEC_C_auto_resend.md)
   * — реально применённое значение (значимо только для `mode: 'full_pipeline'`,
   * см. `ReplaceDesignSourceUseCase.execute`).
   */
  resend_photos: boolean
  warnings: string[]
}

export const designsV2API = {
  /** Список макетов. Возвращает { designs, total }. */
  async getDesigns(
    params: DesignsV2ListParams,
    signal?: AbortSignal,
  ): Promise<{ designs: any[]; total: number; articles_matched?: string[] }> {
    const response = await api.get('/designs', { params, signal })
    const data = unwrap(response) || {}
    const designs = Array.isArray(data) ? data : data.designs || []
    return {
      designs,
      total: Number(data.total) || designs.length,
      articles_matched: Array.isArray(data.articles_matched) ? data.articles_matched : undefined,
    }
  },

  /**
   * Проверка списка артикулов/кодов макетов без загрузки каталога:
   * тот же GET /designs с articles_resolve=1 — сервер игнорирует прочие фильтры.
   */
  async resolveArticles(values: string[]): Promise<string[]> {
    if (values.length === 0) return []
    const r = await designsV2API.getDesigns({
      limit: 1,
      offset: 0,
      articles: values.join(','),
      articles_resolve: '1',
    })
    return r.articles_matched ?? []
  },

  /** Фасетные счётчики для текущего набора серверных фильтров. */
  async getFacets(params: DesignsV2ListParams, signal?: AbortSignal): Promise<DesignFacets> {
    const response = await api.get('/designs/facets', { params, signal })
    return unwrap(response) || {}
  },

  /** Справочники значений для фильтров */
  async getFilterOptions(): Promise<DesignFilterOptions> {
    const response = await api.get('/designs/filter-options')
    const data = unwrap(response) || {}
    const list = (key: keyof DesignFilterOptions): string[] =>
      Array.isArray(data[key]) ? data[key].map(String).filter(Boolean) : []
    return {
      types: list('types'),
      categories: list('categories'),
      brands: list('brands'),
      genders: list('genders'),
      ages: list('ages'),
      occasions: list('occasions'),
      legalStatuses: list('legalStatuses'),
      designStates: list('designStates'),
      seasons: list('seasons'),
      languages: list('languages'),
      recipientStatuses: list('recipientStatuses'),
      personas: list('personas'),
      seoStatuses: list('seoStatuses'),
    }
  },

  async getDesignTypes(): Promise<string[]> {
    const response = await api.get('/design-types')
    const data = unwrap(response)
    const list = Array.isArray(data) ? data : data?.designTypes || []
    return [...new Set(list.map((dt: any) => dt.design_type).filter(Boolean))] as string[]
  },

  /**
   * «Обновить комплекты»: сверка каталога с матрицей типов макетов.
   * preview — что произойдёт, refresh — запуск задания (возвращает jobId).
   */
  previewDesignKits: (): Promise<DesignKitsPreview> =>
    api.get('/products/design-kits/preview').then(unwrap),
  refreshDesignKits: (): Promise<DesignKitsRefreshStart> =>
    api.post('/products/design-kits/refresh').then(unwrap),

  getDesign: (designCode: number | string) => api.get(`/designs/${designCode}`).then(unwrap),
  updateDesign: (designCode: number | string, updates: any) =>
    api.put(`/designs/${designCode}`, updates).then(unwrap),
  replaceDesignSource: (
    designCode: number | string,
    payload: {
      pdfFile: string
      pdfFileName: string
      pdfMimeType?: string
      mode: ReplaceDesignSourceMode
      /**
       * «Отправить новые фото в опубликованные карточки» — по умолчанию
       * включено на бэкенде (`resend_photos !== false`); значимо только для
       * `mode: 'full_pipeline'`.
       */
      resend_photos?: boolean
    },
  ): Promise<ReplaceDesignSourceStart> =>
    api.post(`/designs/${designCode}/source`, payload).then(unwrap),
  createDesign: (body: any) => api.post('/designs', body).then(unwrap),
  duplicateDesign: (designCode: number | string) =>
    api.post(`/designs/${designCode}/duplicate`).then(unwrap),
  deleteDesign: (designCode: number | string) => api.delete(`/designs/${designCode}`).then(unwrap),
  massUpdate: (selection: DesignSelectionPayload, updates: any) =>
    api.put('/designs/mass-update', { ...selection, updates }).then(unwrap),
  massDelete: (selection: DesignSelectionPayload) =>
    api.post('/designs/mass-delete', selection).then(unwrap),

  /**
   * Полное удаление: инвентарь до операции и сама операция.
   * Доступно только администратору и владельцу; confirm — код макета (когда
   * он один) либо слово УДАЛИТЬ, ту же фразу проверяет сервер.
   */
  purgePreview: (designCodes: Array<number | string>): Promise<{ items: DesignPurgePreviewItem[] }> =>
    api.post('/designs/purge-preview', { designCodes }).then(unwrap),
  purgeDesigns: (
    designCodes: Array<number | string>,
    confirm: string,
  ): Promise<DesignPurgeResult> =>
    api.post('/designs/purge', { designCodes, confirm }).then(unwrap),

  /** Экспорт CSV/XLSX: без selection — весь tenant scope, иначе explicit/all. */
  exportDesigns: (format: DesignExportFormat, selection?: DesignSelectionPayload) =>
    api.get('/designs/export', {
      params: selection
        ? 'filters' in selection
          ? { format, filters: JSON.stringify(selection.filters) }
          : { format, design_codes: selection.design_codes.join(',') }
        : { format },
      responseType: 'blob',
    }),

  /** Файлы для печати макета (вкладка карточки) */
  async getProductionFiles(designCode: number | string): Promise<DesignProductionFiles> {
    const response = await api.get(`/designs/${encodeURIComponent(designCode)}/production-files`)
    const data = unwrap(response) || {}
    return {
      files: Array.isArray(data.files) ? data.files : [],
      errors: Array.isArray(data.errors) ? data.errors : [],
      warnings: Array.isArray(data.warnings) ? data.warnings : [],
    }
  },

  /** События конвейера макета (вкладка «История» карточки), новые сверху */
  async getDesignEvents(designCode: number | string): Promise<any[]> {
    const response = await api.get(`/pipeline/${encodeURIComponent(designCode)}/events`)
    const data = unwrap(response) || {}
    return Array.isArray(data.events) ? data.events : []
  },

  // ── Теги ──
  async getTags(): Promise<DesignTag[]> {
    const response = await api.get('/designs/tags')
    const data = unwrap(response) || {}
    return Array.isArray(data) ? data : data.tags || []
  },
  createTag: (value: string, color: string): Promise<DesignTag> =>
    api.post('/designs/tags', { value, color }).then(unwrap),
  setDesignTags: (designCode: number | string, tagIds: number[]) =>
    api.put(`/designs/${designCode}/tags`, { tag_ids: tagIds }).then(unwrap),
  bulkTags: (selection: DesignSelectionPayload, add: number[], remove: number[]) =>
    api.post('/designs/bulk-tags', { ...selection, add, remove }).then(unwrap),
}
